import {
    AwesomeTableComponent,
    Badge,
    HeaderTable,
    IColumnsProps,
    TabBar,
    TimeUtils,
    useFirstTime,
    ViewLabelStatus,
} from "d-react-components";
import { debounce, find, map } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { JsonParam, useQueryParam } from "use-query-params";
import WorksheetAPI from "../../../api/worksheet/WorksheetAPI";
import AppLink from "../../../common/AppLink";
import TableAction from "../../../common/TableActions";
import { PERMISSION_MODULE, ROLE_ACCESS_TYPE } from "../../../constant/user";
import { WORKSHEET_STATUSES } from "../../../constant/worksheet";
import { useUserPermission } from "../../../hoook/permission";
import { IBooking } from "../../../interfaces/booking";
import { IUser } from "../../../interfaces/user";
import Messages from "../../../languages/Messages";
import { isFilteredBody } from "../../../utils/Utils";
import Path from "../../Path";
import WorksheetFilterModal from "./WorksheetFilterModal";

const ALL_TAB: any = { id: "ALL", label: "all" };

interface Props {
    dataSource: any;
    hideSummary?: boolean;
}

const WorksheetTable = ({ dataSource, hideSummary }: Props) => {
    const [worksheetSummary, setWorksheetSummary] = useState([]);
    const history = useHistory();
    const tableRef = useRef<any>(null);
    const searchRef = useRef<string>("");
    const isFirstTime = useFirstTime();

    const [openFilter, setOpenFilter] = useState(false);

    const [filterState, setFilterState] = useQueryParam(
        "filterState",
        JsonParam
    );

    const isGrantDetail = useUserPermission(
        PERMISSION_MODULE.WORKSHEET,
        ROLE_ACCESS_TYPE.DETAIL
    );

    const isGrantDetailBooking = useUserPermission(
        PERMISSION_MODULE.BOOKING,
        ROLE_ACCESS_TYPE.DETAIL
    );

    const isGrantCreate = useUserPermission(
        PERMISSION_MODULE.WORKSHEET,
        ROLE_ACCESS_TYPE.CREATE
    );

    const columns: IColumnsProps = [
        {
            title: Messages.worksheetNo,
            dataIndex: "worksheetNo",
            render: (worksheetNo: string, worksheet: any) => (
                <AppLink
                    disabled={!isGrantDetail}
                    to={generatePath(Path.WORKSHEET_DETAIL, {
                        worksheetId: worksheet.id,
                    })}
                >
                    {worksheetNo}
                </AppLink>
            ),
        },
        {
            title: Messages.bookingNo,
            dataIndex: "booking",
            render: (booking: any) => (
                <AppLink
                    disabled={!isGrantDetailBooking}
                    to={generatePath(Path.BOOKING_DETAIL, {
                        bookingId: booking.id,
                    })}
                >
                    {booking?.bookingNo}
                </AppLink>
            ),
        },
        {
            title: Messages.status,
            dataIndex: "status",
            render: (status: string) => (
                <ViewLabelStatus
                    status={status}
                    listStatus={WORKSHEET_STATUSES}
                    getLabel={(item) => item.label.toUpperCase()}
                />
            ),
        },
        {
            title: Messages.customer,
            dataIndex: "booking",
            render: (booking: IBooking) =>
                `${booking?.customer?.firstName} ${booking?.customer?.lastName}`,
        },

        {
            title: Messages.openingCost,
            dataIndex: "openingCost",
            render: (cost: any) => cost ?? 0,
        },
        {
            title: Messages.itemQty,
            dataIndex: "quantity",
            render: (quantity: any) => quantity,
        },
        {
            title: Messages.serviceDate,
            dataIndex: "serviceDate",
            render: (serviceDate: any) => TimeUtils.toDate(serviceDate),
        },
        {
            title: Messages.createdBy,
            dataIndex: "createBy",
            render: (createBy: IUser) => createBy?.firstName,
        },
        {
            title: Messages.createdAt,
            dataIndex: "createdAt",
            render: TimeUtils.toDateTime,
        },
        {
            title: Messages.action,
            dataIndex: "",
            render: (worksheet) => (
                <TableAction
                    onClick={(actionId: string) =>
                        onClickTableAction(actionId, worksheet)
                    }
                />
            ),
            align: "center",
        },
    ];

    useEffect(() => {
        loadWorksheetSummary();
    }, []);

    useEffect(() => {
        if (isFirstTime) return;
        tableRef.current.refresh();
    }, [filterState, dataSource]);

    const loadWorksheetSummary = () => {
        WorksheetAPI.summary().then(setWorksheetSummary);
    };

    const source = (pagingData: any) => {
        const isAll =
            !filterState?.status || filterState?.status?.id === ALL_TAB.id;

        const filterParams = {
            status: isAll ? [] : filterState?.status.id,

            createdAtFrom: filterState?.createdAtFrom
                ? moment(filterState?.createdAtFrom).toISOString()
                : null,
            createdAtTo: filterState?.createdAtTo
                ? moment(filterState?.createdAtTo).toISOString()
                : null,
            createBy: map(filterState?.createBy, (item) => item.id),
            voucherCodes: map(filterState?.createBy, (item) => item.id),
            PIC: map(filterState?.PIC, (item) => item.id),
            serviceCategories: filterState?.serviceCategories,
            sourceOfBooking: map(filterState?.source, (item) => item.id),
            customerIds: map(filterState?.customer, (item) => item.id),
        };

        if (!dataSource) return Promise.resolve([]);

        return dataSource(searchRef.current, pagingData, filterParams);

        // return WorksheetAPI.list(searchRef.current, pagingData, filterParams);
    };

    const onClickTableAction = (actionId: string, worksheet: any) => {
        switch (actionId) {
            default:
                history.push(
                    generatePath(Path.WORKSHEET_DETAIL, {
                        worksheetId: worksheet.id,
                    })
                );
        }
    };

    const onClickAddNew = () => {
        history.push(Path.WORKSHEET_CREATE);
    };

    const onChangeSearch = debounce((text) => {
        searchRef.current = text;
        tableRef.current.refresh();
    }, 500);

    const renderLabelTab = (tab: any) => {
        const summary = find(
            worksheetSummary,
            (item: any) => item.type === tab.id
        );
        return (
            <div className="flex-center">
                {(Messages as any)[tab.label]}
                {!hideSummary && (
                    <Badge
                        className="ml-3"
                        variant="index"
                        size="xx-large"
                        index={summary?.total}
                        color={summary?.total === 0 ? "green" : "secondary"}
                    />
                )}
            </div>
        );
    };

    const onFilter = (values: any) => {
        setFilterState(values);
        tableRef.current.refresh();
    };

    return (
        <div className="p-4">
            <HeaderTable
                // onClickNew={onClickAddNew}
                label={Messages.worksheet}
                onClickFilter={() => setOpenFilter(true)}
                onChangeText={(event: any) =>
                    onChangeSearch(event.target.value)
                }
                className="mb-3"
                // showAddNew={isGrantCreate}
                isFiltered={isFilteredBody(filterState)}
            />
            <TabBar
                dataSource={[ALL_TAB, ...WORKSHEET_STATUSES]}
                onChange={(tab) => setFilterState({ status: tab as any })}
                value={filterState?.status ?? ALL_TAB}
                getLabel={renderLabelTab}
            />
            <AwesomeTableComponent
                ref={(ref) => {
                    tableRef.current = ref;
                }}
                source={source}
                columns={columns}
                showSelectColumn
                transformer={(res) => res?.data?.data?.worksheet ?? []}
                baseColumnProps={{ width: 100 }}
                bordered={false}
                className="bg-white"
            />

            {openFilter && (
                <WorksheetFilterModal
                    open={openFilter}
                    onClose={() => setOpenFilter(false)}
                    onFilter={onFilter}
                    defaultValue={filterState}
                />
            )}
        </div>
    );
};

export default WorksheetTable;
